import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, excelDate, seededRandom } from "../spec";

const SHEET = "AG Tablero";
const TABLE = "PedidosAG";
const REGIONES_TABLE = "ResumenRegiones";
const VENDEDORES_TABLE = "ResumenVendedores";

const REGIONES = ["Norte", "Sur", "Centro", "Occidente"] as const;
const VENDEDORES = ["Ana López", "Luis Martínez", "Marta Sánchez", "Jorge Ramírez", "Sofía Torres", "Diego Castillo"] as const;
const CLIENTES = [
  "Grupo Altamira", "Comercial Norte", "Distribuidora Luna", "Hotel Mirador", "Farmacias Vida",
  "Constructora Roble", "Café La Esquina", "Papelería Central", "Transportes Águila", "Clínica San José",
] as const;

const rnd = seededRandom(4242);
const START = excelDate("2026-01-05");
// Pedido, Fecha, Cliente, Región, Vendedor, Importe, Pago, Devuelto
const ROWS = Array.from({ length: 72 }, () =>
  [
    "",
    START + rnd.int(0, 170),
    rnd.pick(CLIENTES),
    rnd.pick(REGIONES),
    rnd.pick(VENDEDORES),
    rnd.round(4000, 95000, 50),
    rnd.next() < 0.28 ? "Pendiente" : "Pagado",
    rnd.next() < 0.1 ? "Sí" : "No",
  ] as [string, number, string, string, string, number, string, string]
)
  .sort((a, b) => a[1] - b[1])
  .map((row, i) => [`PED-${7001 + i}`, ...row.slice(1)] as typeof row);

type Row = (typeof ROWS)[number];
const sum = (filter: (r: Row) => boolean) => ROWS.filter(filter).reduce((s, r) => s + r[5], 0);
const cnt = (filter: (r: Row) => boolean) => ROWS.filter(filter).length;

const neto = (reg: string) => sum((r) => r[3] === reg && r[7] === "No");
const pedidos = (reg: string) => cnt((r) => r[3] === reg && r[7] === "No");
const pendiente = (reg: string) => sum((r) => r[3] === reg && r[7] === "No" && r[6] === "Pendiente") / neto(reg);

// Meta = mediana de ventas netas, para que haya regiones de los dos lados.
const netos = REGIONES.map(neto).sort((a, b) => a - b);
const META = Math.round((netos[1] + netos[2]) / 2 / 10000) * 10000;
const LIMITE = 0.25;
const T1 = { desde: excelDate("2026-01-01"), hasta: excelDate("2026-03-31") };

const ventasT1 = (v: string) =>
  sum((r) => r[4] === v && r[1] >= T1.desde && r[1] <= T1.hasta && r[7] === "No");
const mejorT1 = [...VENDEDORES].sort((a, b) => ventasT1(b) - ventasT1(a))[0];

export const spec: FuncionProjectSpec = {
  projectId: "funciones-tablero-agregacion",
  sheets: [
    {
      name: SHEET,
      title: "Tablero comercial",
      subtitle: "Pedidos del primer semestre — arma el tablero que verá dirección cada lunes",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Pedido", "Fecha", "Cliente", "Región", "Vendedor", "Importe", "Pago", "Devuelto"],
          rows: ROWS,
          formats: { Fecha: "dd/mm/yyyy", Importe: "$#,##0" },
        },
        {
          name: REGIONES_TABLE,
          anchor: "J4",
          headers: ["Región"],
          rows: REGIONES.map((r) => [r]),
          answerColumns: ["Ventas netas", "Pedidos", "Ticket promedio", "% pendiente", "Estado"],
          formats: {
            "Ventas netas": "$#,##0",
            "Ticket promedio": "$#,##0",
            "% pendiente": "0.0%",
          },
        },
        {
          name: VENDEDORES_TABLE,
          anchor: "J12",
          headers: ["Vendedor"],
          rows: VENDEDORES.map((v) => [v]),
          answerColumns: ["Ventas T1"],
          formats: { "Ventas T1": "$#,##0" },
        },
      ],
      inputs: [
        { address: "R4", label: "Meta por región", value: META, format: "$#,##0" },
        { address: "R5", label: "Límite pendiente de cobro", value: LIMITE, format: "0%" },
        { address: "R7", label: "Inicio T1", value: T1.desde, format: "dd/mm/yyyy" },
        { address: "R8", label: "Fin T1", value: T1.hasta, format: "dd/mm/yyyy" },
      ],
      answers: [{ address: "R11", label: "Tarea 7 · Mejor vendedor T1" }],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: REGIONES_TABLE, column: "Ventas netas", fns: ["SUMIFS"],
      expected: REGIONES.map(neto),
    },
    2: {
      kind: "column", sheet: SHEET, table: REGIONES_TABLE, column: "Pedidos", fns: ["COUNTIFS"],
      expected: REGIONES.map(pedidos),
    },
    3: {
      kind: "column", sheet: SHEET, table: REGIONES_TABLE, column: "Ticket promedio", fns: ["AVERAGEIFS"],
      expected: REGIONES.map((reg) => neto(reg) / pedidos(reg)),
    },
    4: {
      kind: "column", sheet: SHEET, table: REGIONES_TABLE, column: "% pendiente", fns: ["SUMIFS"],
      expected: REGIONES.map(pendiente),
    },
    5: {
      kind: "column", sheet: SHEET, table: REGIONES_TABLE, column: "Estado", fns: ["IF", "AND"],
      expected: REGIONES.map((reg) => (neto(reg) >= META && pendiente(reg) <= LIMITE ? "Saludable" : "Atención")),
    },
    6: {
      kind: "column", sheet: SHEET, table: VENDEDORES_TABLE, column: "Ventas T1", fns: ["SUMIFS"],
      expected: VENDEDORES.map(ventasT1),
    },
    7: {
      kind: "cell", sheet: SHEET, address: "R11", fns: ["XLOOKUP|INDEX|VLOOKUP", "MAX"],
      expected: mejorT1,
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-tablero-agregacion",
  title: "Reto final: Tablero comercial",
  subtitle: "SUMAR, CONTAR y PROMEDIO.SI.CONJUNTO juntos",
  description:
    "Dirección quiere un tablero que se actualice solo cada lunes: ventas netas por región (sin devoluciones), número de pedidos, ticket promedio, cuánto falta por cobrar, un semáforo por región y el mejor vendedor del trimestre.\n\nCada columna usa una función distinta de esta categoría, y las últimas las combinan con SI, Y y BUSCARX.",
  topics: [
    "Excluir devoluciones en todas las métricas",
    "Ticket promedio con PROMEDIO.SI.CONJUNTO",
    "Porcentajes construidos con dos SUMAR.SI.CONJUNTO",
    "Semáforo con SI y Y",
    "Ranking: MAX + BUSCARX sobre un resumen",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Ventas netas por región",
      description: conditions(
        "En la hoja <strong>AG Tablero</strong>, tabla <strong>ResumenRegiones</strong>, columna <strong>Ventas netas</strong>: suma el Importe de cada región considerando:",
        ["Región igual a la de la fila (<strong>[@Región]</strong>)", "<strong>Devuelto = No</strong> (las devoluciones no cuentan)"]
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")')}.`,
      ],
      expectedResult: "Ventas sin devoluciones de cada región",
      finalFormula: '=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")',
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Pedidos por región",
      description:
        "En la columna <strong>Pedidos</strong>: cuenta los pedidos de cada región que <strong>no</strong> fueron devueltos.",
      solution: [
        `Selecciona ${F("L5")}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO(PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")')}.`,
      ],
      expectedResult: "Número de pedidos netos por región",
      finalFormula: '=CONTAR.SI.CONJUNTO(PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")',
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Ticket promedio",
      description:
        "En la columna <strong>Ticket promedio</strong>: el Importe promedio por pedido de cada región, sin devoluciones. Usa <strong>PROMEDIO.SI.CONJUNTO</strong>.",
      solution: [
        `Selecciona ${F("M5")}.`,
        `Escribe ${F('=PROMEDIO.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")')}.`,
        "Comprueba: debe ser igual a Ventas netas ÷ Pedidos.",
      ],
      expectedResult: "Importe promedio por pedido",
      finalFormula: '=PROMEDIO.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No")',
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: % pendiente de cobro",
      description: conditions(
        "En la columna <strong>% pendiente</strong>: qué parte de las ventas netas de cada región aún no se cobra.",
        [
          "Arriba: Importe de la región, <strong>Devuelto = No</strong> y <strong>Pago = Pendiente</strong>",
          "Abajo: las <strong>Ventas netas</strong> de la Tarea 1",
        ]
      ),
      solution: [
        `Selecciona ${F("N5")}.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No",PedidosAG[Pago],"Pendiente")/[@[Ventas netas]]')}.`,
      ],
      expectedResult: "Porcentaje por cobrar de cada región",
      finalFormula:
        '=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Región],[@Región],PedidosAG[Devuelto],"No",PedidosAG[Pago],"Pendiente")/[@[Ventas netas]]',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Semáforo por región",
      description: conditions(
        "En la columna <strong>Estado</strong>: escribe <strong>Saludable</strong> si se cumplen <strong>las dos</strong> condiciones; si no, <strong>Atención</strong>.",
        [
          "Ventas netas <strong>mayores o iguales</strong> a la meta de <strong>R4</strong>",
          "% pendiente <strong>menor o igual</strong> al límite de <strong>R5</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("O5")}.`,
        `Usa referencias absolutas para la meta y el límite: ${F("$R$4")} y ${F("$R$5")}.`,
        `Escribe ${F('=SI(Y([@[Ventas netas]]>=$R$4,[@[% pendiente]]<=$R$5),"Saludable","Atención")')}.`,
      ],
      expectedResult: "Saludable / Atención",
      finalFormula: '=SI(Y([@[Ventas netas]]>=$R$4,[@[% pendiente]]<=$R$5),"Saludable","Atención")',
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Ventas del primer trimestre",
      description: conditions(
        "En la tabla <strong>ResumenVendedores</strong> (J12), columna <strong>Ventas T1</strong>: suma el Importe de cada vendedor considerando:",
        [
          "Vendedor de la fila (<strong>[@Vendedor]</strong>)",
          "Fecha <strong>desde R7</strong> y <strong>hasta R8</strong>",
          "<strong>Devuelto = No</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("K13")}.`,
        `Escribe ${F('=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Vendedor],[@Vendedor],PedidosAG[Fecha],">="&$R$7,PedidosAG[Fecha],"<="&$R$8,PedidosAG[Devuelto],"No")')}.`,
      ],
      expectedResult: "Ventas netas de enero a marzo por vendedor",
      finalFormula:
        '=SUMAR.SI.CONJUNTO(PedidosAG[Importe],PedidosAG[Vendedor],[@Vendedor],PedidosAG[Fecha],">="&$R$7,PedidosAG[Fecha],"<="&$R$8,PedidosAG[Devuelto],"No")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Mejor vendedor del trimestre",
      description:
        "En <strong>R11</strong>: devuelve el <strong>nombre</strong> del vendedor con más <strong>Ventas T1</strong>. Combina <strong>MAX</strong> (para encontrar la venta más alta) con <strong>BUSCARX</strong> (para traer el nombre de esa fila).",
      solution: [
        `Selecciona ${F("R11")}.`,
        `${F("MAX(ResumenVendedores[Ventas T1])")} da el monto más alto; BUSCARX lo busca en la misma columna y devuelve el vendedor.`,
        `Escribe ${F("=BUSCARX(MAX(ResumenVendedores[Ventas T1]),ResumenVendedores[Ventas T1],ResumenVendedores[Vendedor])")}.`,
      ],
      expectedResult: "Nombre del mejor vendedor de enero a marzo",
      finalFormula:
        "=BUSCARX(MAX(ResumenVendedores[Ventas T1]),ResumenVendedores[Ventas T1],ResumenVendedores[Vendedor])",
    },
  ],
};
